import React, { useEffect, useMemo, useState } from 'react';
import { apiCall } from '../../../../utils/apiClient';

interface Partner {
  id: number;
  name: string;
  location?: string;
  assignedWorkInProgress?: number | string;
  completed?: number | string;
  rejected?: number | string;
  assignedLocation?: string;
  teamSize?: number | string;
  lastTask?: string;
  profession?: string;
}

interface AssignPartnerModalProps {
  isOpen: boolean;
  onClose: () => void;
  jobTitle: string;
  jobNumber: string;
  servicesCount: number;
  onAssign: (partners: { id: number; name: string }[]) => void;
}

const AssignPartnerModal: React.FC<AssignPartnerModalProps> = ({
  isOpen,
  onClose,
  jobTitle,
  jobNumber,
  servicesCount,
  onAssign,
}) => {
  const [partners, setPartners] = useState<Partner[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedProfession, setSelectedProfession] = useState('');
  const [selectedPartnerIds, setSelectedPartnerIds] = useState<number[]>([]);

  useEffect(() => {
    if (!isOpen) return;

    const fetchPartners = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const res = await apiCall({ endpoint: 'admin/partners/summary', method: 'GET' });
        const data = (res?.data?.data ?? res?.data ?? []).map((partner: any) => ({
          id: Number(partner.id),
          name: partner.name,
          location: partner.location,
          assignedWorkInProgress: partner.in_progress,
          completed: partner.completed,
          rejected: partner.rejected,
          assignedLocation: partner.assigned_location,
          teamSize: partner.team_size,
          lastTask: partner.last_task,
          profession: partner.profession,
        }));
        setPartners(data);
      } catch (err: any) {
        setError(err?.message || 'Failed to fetch partners');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPartners();
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return;
    setSelectedPartnerIds([]);
    setSearchTerm('');
    setSelectedProfession('');
  }, [isOpen]);

  const professions = useMemo(() => {
    return Array.from(new Set(partners.map((p) => p.profession).filter(Boolean))) as string[];
  }, [partners]);

  const filteredPartners = useMemo(() => {
    return partners.filter((partner) => {
      const matchesSearch = partner.name?.toLowerCase().includes(searchTerm.toLowerCase());
      const matchesProfession = selectedProfession ? partner.profession === selectedProfession : true;
      return matchesSearch && matchesProfession;
    });
  }, [partners, searchTerm, selectedProfession]);

  const selectedPartners = partners.filter((p) => selectedPartnerIds.includes(p.id));

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg w-full max-w-5xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b flex justify-between items-center">
          <h3 className="text-lg font-bold text-gray-800">Assign Partner</h3>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 text-xl">
            ✕
          </button>
        </div>

        <div className="p-6 space-y-4 overflow-y-auto">
          <div className="bg-purple-50 border border-purple-200 rounded p-3">
            <p className="text-sm font-semibold text-gray-800 mb-1">{jobTitle}</p>
            <p className="text-xs text-gray-600">Job: {jobNumber}</p>
            <p className="text-xs text-gray-500 mt-1">{servicesCount} service(s) in this job</p>
          </div>

          <div className="flex flex-col md:flex-row gap-3 items-center">
            <select
              className="border rounded p-2 text-sm w-full md:w-56"
              value={selectedProfession}
              onChange={(e) => setSelectedProfession(e.target.value)}
            >
              <option value="">All Professions</option>
              {professions.map((profession) => (
                <option key={profession} value={profession}>
                  {profession}
                </option>
              ))}
            </select>

            <input
              type="text"
              placeholder="Search Partner"
              className="border rounded p-2 text-sm w-full"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          {isLoading ? (
            <div className="bg-gray-50 border border-gray-200 rounded-lg p-6 text-center">
              <div className="inline-block">
                <div className="w-6 h-6 border-4 border-gray-300 border-t-blue-600 rounded-full animate-spin"></div>
              </div>
              <p className="text-gray-600 mt-3 text-sm">Loading partners...</p>
            </div>
          ) : error ? (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-700 text-sm"><strong>Error:</strong> {error}</p>
            </div>
          ) : (
            <div className="border border-gray-200 rounded-lg overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200 text-xs">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Select</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Name</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Location</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Assigned (WIP)</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Completed</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Rejected</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Assigned Location</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Team Size</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Last Task</th>
                    <th className="px-3 py-2 text-left font-semibold text-gray-700">Profession</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredPartners.length === 0 ? (
                    <tr>
                      <td colSpan={10} className="px-3 py-6 text-center text-gray-500 text-sm">
                        No partners found.
                      </td>
                    </tr>
                  ) : (
                    filteredPartners.map((partner) => (
                      <tr
                        key={partner.id}
                        className={`hover:bg-gray-50 ${selectedPartnerIds.includes(partner.id) ? 'bg-blue-50' : ''}`}
                      >
                        <td className="px-3 py-2">
                          <input
                            type="checkbox"
                            checked={selectedPartnerIds.includes(partner.id)}
                            onChange={() =>
                              setSelectedPartnerIds((prev) =>
                                prev.includes(partner.id)
                                  ? prev.filter((id) => id !== partner.id)
                                  : [...prev, partner.id]
                              )
                            }
                          />
                        </td>
                        <td className="px-3 py-2 font-medium text-gray-900">{partner.name}</td>
                        <td className="px-3 py-2 text-gray-700">{partner.location || '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{partner.assignedWorkInProgress ?? '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{partner.completed ?? '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{partner.rejected ?? '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{partner.assignedLocation || '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{partner.teamSize ?? '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{partner.lastTask || '-'}</td>
                        <td className="px-3 py-2 text-gray-700">{partner.profession || '-'}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}

          <div className="bg-yellow-50 border border-yellow-200 rounded p-3">
            <p className="text-xs text-yellow-800">
              <span className="font-semibold">📤 Note:</span> This partner will be assigned to complete all services in this job. A request will be sent to the partner for acceptance.
            </p>
          </div>
        </div>

        <div className="px-6 py-4 border-t flex justify-end gap-3">
          <button onClick={onClose} className="px-4 py-2 text-sm border border-gray-300 rounded hover:bg-gray-50">
            Cancel
          </button>
          <button
            onClick={() => selectedPartners.length > 0 && onAssign(selectedPartners.map((p) => ({ id: p.id, name: p.name })))}
            disabled={selectedPartners.length === 0}
            className="px-6 py-2 text-sm bg-blue-600 text-white rounded hover:bg-blue-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Assign Partner
          </button>
        </div>
      </div>
    </div>
  );
};

export default AssignPartnerModal;
